import type { ReactNode } from 'react';

/**
 * The Markdown the guides use, and nothing more: `#`–`###` headings, paragraphs, `-` / `1.` lists, fenced code, pipe
 * tables, `> ` notes and inline `code`, **bold** and [links](url). `{{key}}` is replaced by `values[key]`; an unknown
 * key stays visible as `{{key}}`, so a printed guide shows what is still to fill in.
 */
export function fill(text: string, values: Record<string, string | undefined>): string {
  // an unknown value reads as `<print server name>` (from `{{printServerName}}`), also on paper
  return text.replace(
    /\{\{(\w+)\}\}/g,
    (_, k: string) => values[k] || `<${k.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()}>`
  );
}

function inline(text: string, key = 0): ReactNode[] {
  const out: ReactNode[] = [];
  const rx = /`([^`]+)`|\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = rx.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const k = `${key}-${i++}`;
    if (m[1] !== undefined) out.push(<code key={k}>{m[1]}</code>);
    else if (m[2] !== undefined) out.push(<strong key={k}>{inline(m[2], i)}</strong>);
    else
      out.push(
        <a key={k} href={m[4]} target="_blank" rel="noreferrer" className="nyk-link">
          {m[3]}
        </a>
      );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const cells = (row: string) =>
  row
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((c) => c.trim());

export function Markdown({ text }: { text: string }) {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const k = blocks.length;
    if (!line.trim()) {
      i++;
    } else if (line.startsWith('```')) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) body.push(lines[i++]);
      i++;
      blocks.push(
        <pre key={k}>
          <code>{body.join('\n')}</code>
        </pre>
      );
    } else if (/^#{1,3} /.test(line)) {
      const level = line.indexOf(' ');
      const content = inline(line.slice(level + 1));
      blocks.push(
        level === 1 ? (
          <h1 key={k}>{content}</h1>
        ) : level === 2 ? (
          <h2 key={k}>{content}</h2>
        ) : (
          <h3 key={k}>{content}</h3>
        )
      );
      i++;
    } else if (/^\s*([-*]|\d+\.) /.test(line)) {
      const ordered = /^\s*\d+\. /.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*([-*]|\d+\.) /.test(lines[i])) {
        let item = lines[i++].replace(/^\s*([-*]|\d+\.) /, '');
        while (i < lines.length && /^ {2,}\S/.test(lines[i]) && !/^\s*([-*]|\d+\.) /.test(lines[i]))
          item += ` ${lines[i++].trim()}`;
        items.push(item);
      }
      const lis = items.map((t, j) => <li key={j}>{inline(t, j)}</li>);
      blocks.push(ordered ? <ol key={k}>{lis}</ol> : <ul key={k}>{lis}</ul>);
    } else if (line.trim().startsWith('|')) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        if (!/^\s*\|[\s:|-]+\|\s*$/.test(lines[i])) rows.push(cells(lines[i]));
        i++;
      }
      const [head, ...body] = rows;
      blocks.push(
        <div key={k} className="nyk-scroll">
          <table>
            <thead>
              <tr>
                {head.map((c, j) => (
                  <th key={j}>{inline(c, j)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((r, j) => (
                <tr key={j}>
                  {r.map((c, n) => (
                    <td key={n}>{inline(c, n)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    } else if (line.startsWith('> ')) {
      const body: string[] = [];
      while (i < lines.length && lines[i].startsWith('> ')) body.push(lines[i++].slice(2));
      blocks.push(
        <div key={k} className="ob-note">
          {inline(body.join(' '))}
        </div>
      );
    } else {
      const body: string[] = [];
      while (
        i < lines.length &&
        lines[i].trim() &&
        !/^(#{1,3} |```|> |\s*([-*]|\d+\.) |\s*\|)/.test(lines[i])
      )
        body.push(lines[i++].trim());
      blocks.push(<p key={k}>{inline(body.join(' '))}</p>);
    }
  }
  return <>{blocks}</>;
}
